(() => {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let lastDrawerTrigger = null;
  let cartQueue = Promise.resolve();
  const drawerTimers = new WeakMap();
  const inertElements = new Map();
  const themeConfig = JSON.parse(document.querySelector('[data-storefront-config]')?.textContent || '{}');
  const message = (key, fallback) => themeConfig.messages?.[key] || fallback;
  const rootUrl = () => window.Shopify?.routes?.root || themeConfig.routes?.root || '/';
  const storefrontUrl = (path) => `${rootUrl().replace(/\/?$/, '/')}${path}`;
  const enqueueCart = (task) => { cartQueue = cartQueue.catch(() => {}).then(task); return cartQueue; };
  let searchAbortController = null;
  let privacyApi = null;
  let immersiveScrollInitialized = false;
  let customCursorInitialized = false;
  let videoVisibilityInitialized = false;
  let pageTransitionsInitialized = false;
  const wishlistKey = 'sidhita-wishlist';

  const liveAnnounce = (message) => {
    const region = qs('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  const syncDrawerBackground = (drawer) => {
    inertElements.forEach((value, element) => { element.inert = value; });
    inertElements.clear();
    if (!drawer) return;
    [...document.body.children].forEach((element) => {
      if (element === drawer || element.contains(drawer) || element.matches('script, style, [data-live-region]')) return;
      inertElements.set(element, element.inert);
      element.inert = true;
    });
  };

  const openDrawer = (drawer, trigger) => {
    if (!drawer) return;
    const wasOpen = drawer.classList.contains('is-open');
    qsa('[data-drawer].is-open').filter((item) => item !== drawer).forEach((item) => closeDrawer(item, false));
    window.clearTimeout(drawerTimers.get(drawer));
    if (trigger && !trigger.closest('[data-drawer]')) lastDrawerTrigger = trigger;
    else if (!lastDrawerTrigger) lastDrawerTrigger = document.activeElement;
    drawer.hidden = false;
    drawer.inert = false;
    drawer.dataset.openRequested = 'true';
    document.body.classList.add('drawer-open');
    syncDrawerBackground(drawer);
    requestAnimationFrame(() => {
      if (drawer.dataset.openRequested !== 'true') return;
      drawer.classList.add('is-open');
      const panel = qs('[role="dialog"]', drawer);
      if (!wasOpen) (qs('[data-predictive-search-input]', panel) || qs(focusableSelector, panel) || panel)?.focus();
    });
    qsa(`[aria-controls="${drawer.id}"]`).forEach((control) => control.setAttribute('aria-expanded', 'true'));
    document.dispatchEvent(new CustomEvent('sidhita:drawer-opened', { detail: { id: drawer.id } }));
  };

  const closeDrawer = (drawer, restoreFocus = true) => {
    if (!drawer) return;
    window.clearTimeout(drawerTimers.get(drawer));
    drawer.dataset.openRequested = 'false';
    drawer.classList.remove('is-open');
    drawer.inert = true;
    qsa(`[aria-controls="${drawer.id}"]`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (!qs('[data-drawer].is-open')) {
      document.body.classList.remove('drawer-open');
      syncDrawerBackground(null);
    }
    drawerTimers.set(drawer, window.setTimeout(() => {
      if (drawer.dataset.openRequested !== 'true') drawer.hidden = true;
    }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 320));
    if (restoreFocus && lastDrawerTrigger?.isConnected) lastDrawerTrigger.focus();
  };

  const trapDrawerFocus = (event, drawer) => {
    if (event.key !== 'Tab') return;
    const panel = qs('[role="dialog"]', drawer);
    const focusable = qsa(focusableSelector, panel).filter((item) => item.getClientRects().length && !item.closest('[hidden], [inert]'));
    if (!focusable.length) { event.preventDefault(); panel?.focus(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
      event.preventDefault(); first.focus();
    }
  };

  const finishPreloader = () => {
    const loader = qs('[data-preloader]');
    if (!loader) return;
    const logo = qs('[data-preloader-logo]', loader);
    const startedAt = performance.now();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const minimumDisplay = reducedMotion ? 350 : 900;
    const maximumLogoWait = 1800;

    const revealLogo = () => loader.classList.add('has-logo');
    const logoReady = new Promise((resolve) => {
      if (!logo) { resolve(); return; }
      if (logo.complete && logo.naturalWidth > 0) {
        revealLogo();
        resolve();
        return;
      }
      const settle = () => { revealLogo(); resolve(); };
      logo.addEventListener('load', settle, { once: true });
      logo.addEventListener('error', resolve, { once: true });
    });
    const waitLimit = new Promise((resolve) => window.setTimeout(resolve, maximumLogoWait));

    Promise.race([logoReady, waitLimit]).then(() => {
      const remaining = Math.max(0, minimumDisplay - (performance.now() - startedAt));
      window.setTimeout(() => {
        loader.classList.add('is-finished');
        window.setTimeout(() => loader.remove(), reducedMotion ? 120 : 650);
      }, remaining);
    });
  };

  const initPageTransitions = () => {
    if (pageTransitionsInitialized) return;
    pageTransitionsInitialized = true;
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') || link.target === '_blank') return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.protocol !== window.location.protocol || (url.pathname === window.location.pathname && url.search === window.location.search && url.hash)) return;
      document.body.classList.add('is-page-leaving');
    });
    window.addEventListener('beforeunload', () => document.body.classList.add('is-page-leaving'));
    window.addEventListener('pageshow', () => {
      document.body.classList.remove('is-page-leaving');
      qsa('[data-cart-form][aria-busy="true"]').forEach((form) => {
        form.removeAttribute('aria-busy');
        qsa('[data-checkout-label]', form).forEach((button) => { button.textContent = button.dataset.checkoutLabel; });
      });
    });
  };

  const updateHeader = () => {
    const header = qs('[data-site-header]');
    if (!header) return;
    const announcement = qs('.announcement');
    const stickyThreshold = (announcement?.offsetHeight || 0) + 8;
    header.classList.toggle('is-scrolled', window.scrollY > stickyThreshold);
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const progress = scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0;
    header.style.setProperty('--scroll-progress', progress.toFixed(4));
  };

  const cartSections = () => [
    { selector: '#CartDrawer', id: qs('#CartDrawer')?.dataset.cartDrawerSection },
    { selector: '[data-cart-page-section]', id: qs('[data-cart-page-section]')?.dataset.cartPageSection }
  ].filter((entry) => entry.id);

  const cartStatus = (text, error = false) => {
    qsa('[data-cart-status]').forEach((node) => {
      node.textContent = text;
      node.classList.toggle('is-error', error);
      node.setAttribute('role', error ? 'alert' : 'status');
    });
    liveAnnounce(text);
  };

  const lockForm = (form) => {
    const states = qsa('button, input, select, textarea', form).map((element) => [element, element.disabled]);
    form.setAttribute('aria-busy', 'true');
    states.forEach(([element]) => { element.disabled = true; });
    return () => {
      form.removeAttribute('aria-busy');
      states.forEach(([element, disabled]) => { element.disabled = disabled; });
    };
  };

  const cartResponse = async (response, fallback) => {
    const result = await response.json().catch(() => null);
    if (!response.ok) throw new Error(typeof result?.description === 'string' ? result.description : typeof result?.message === 'string' ? result.message : fallback);
    if (!result) throw new Error(fallback);
    return result;
  };

  const refreshCartDrawer = async (result, openAfter = false, focusState = null) => {
    let sections = result.sections;
    const entries = cartSections();
    if (entries.some((entry) => !sections?.[entry.id])) {
      const url = new URL(window.location.href);
      url.search = '';
      url.searchParams.set('sections', entries.map((entry) => entry.id).join(','));
      const response = await fetch(url, { credentials: 'same-origin' });
      sections = await cartResponse(response, message('refreshError', 'Unable to refresh your bag.'));
    }
    const cart = typeof result.item_count === 'number' ? result : await fetch(storefrontUrl('cart.js'), { credentials: 'same-origin' }).then((response) => cartResponse(response, message('refreshError', 'Unable to refresh your bag.')));
    const active = document.activeElement;
    const focusKey = focusState?.key || active?.closest('[data-cart-key]')?.dataset.cartKey || active?.dataset.cartKey;
    const focusAction = focusState?.action || (active?.hasAttribute('data-quantity-plus') ? '[data-quantity-plus]' : active?.hasAttribute('data-quantity-minus') ? '[data-quantity-minus]' : '[data-cart-quantity]');
    let restoreWithin = null;
    entries.forEach((entry) => {
      const current = qs(entry.selector);
      const parsed = new DOMParser().parseFromString(sections[entry.id] || '', 'text/html');
      const next = qs(entry.selector, parsed);
      if (!current || !next) throw new Error(message('refreshError', 'Unable to refresh your bag.'));
      const wasOpen = current.classList.contains('is-open');
      const heldFocus = focusState?.selector === entry.selector || current.contains(active);
      const note = qs('[name="note"]', current);
      const nextNote = qs('[name="note"]', next);
      if (note && nextNote) nextNote.value = note.value;
      current.replaceWith(next);
      if (entry.selector === '#CartDrawer' && (wasOpen || openAfter)) {
        openDrawer(next, lastDrawerTrigger);
        if (heldFocus) restoreWithin = next;
      } else if (heldFocus) restoreWithin = next;
    });
    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.setAttribute('aria-label', message('cartCount', '__COUNT__ items in bag').replace('__COUNT__', String(cart.item_count)));
    });
    if (restoreWithin) requestAnimationFrame(() => {
      const line = qsa('[data-cart-key]', restoreWithin).find((element) => element.dataset.cartKey === focusKey && element.matches('article'));
      (qs(focusAction, line || restoreWithin) || qs('[data-cart-focus]', restoreWithin) || qs('[role="dialog"]', restoreWithin))?.focus();
    });
    document.dispatchEvent(new CustomEvent('sidhita:cart-updated', { detail: cart }));
    return cart;
  };

  const animateProductToCart = (form) => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const productRoot = form.closest('[data-product-section], .product-card, article, section');
    const sourceImage = qs('[data-fw27-panel]:not([hidden]) img, [data-product-gallery] .product-media.is-active img, [data-product-gallery] .product-media img, .product-card__media img, img', productRoot || form);
    const cartTrigger = qs('[data-drawer-open="CartDrawer"]');
    if (!sourceImage || !cartTrigger || !sourceImage.animate) return;
    const sourceRect = sourceImage.getBoundingClientRect();
    const targetRect = cartTrigger.getBoundingClientRect();
    if (!sourceRect.width || !sourceRect.height) return;
    const flightWidth = Math.min(sourceRect.width, 180);
    const flightHeight = Math.min(sourceRect.height, 225);
    const flight = sourceImage.cloneNode();
    flight.className = 'cart-flight';
    flight.removeAttribute('srcset');
    flight.removeAttribute('sizes');
    Object.assign(flight.style, {
      top: `${sourceRect.top}px`,
      left: `${sourceRect.left}px`,
      width: `${flightWidth}px`,
      height: `${flightHeight}px`
    });
    document.body.append(flight);
    const targetX = targetRect.left + (targetRect.width / 2) - sourceRect.left - (flightWidth / 2);
    const targetY = targetRect.top + (targetRect.height / 2) - sourceRect.top - (flightHeight / 2);
    flight.animate([
      { opacity: .92, transform: 'translate3d(0,0,0) scale(.58) rotate(0deg)', borderRadius: '0' },
      { opacity: 1, offset: .28, transform: `translate3d(${targetX * .28}px,${targetY * .2 - 34}px,0) scale(.42) rotate(-3deg)`, borderRadius: '22%' },
      { opacity: 0, transform: `translate3d(${targetX}px,${targetY}px,0) scale(.08) rotate(7deg)`, borderRadius: '50%' }
    ], { duration: 760, easing: 'cubic-bezier(.22,.8,.2,1)', fill: 'forwards' }).finished.finally(() => {
      flight.remove();
      cartTrigger.classList.add('is-cart-pulsing');
      window.setTimeout(() => cartTrigger.classList.remove('is-cart-pulsing'), 520);
    });
  };

  const changeCartLine = (key, quantity) => enqueueCart(async () => {
    if (!key || !Number.isInteger(quantity) || quantity < 0) {
      cartStatus(message('quantityError', 'Enter a whole number of items, zero or more.'), true);
      return;
    }
    const active = document.activeElement;
    const focusState = { key, selector: active?.closest('#CartDrawer') ? '#CartDrawer' : active?.closest('[data-cart-page-section]') ? '[data-cart-page-section]' : '', action: active?.hasAttribute('data-quantity-plus') ? '[data-quantity-plus]' : active?.hasAttribute('data-quantity-minus') ? '[data-quantity-minus]' : '[data-cart-quantity]' };
    const unlock = qsa('[data-cart-form]').map(lockForm);
    cartStatus(message('updating', 'Updating your bag…'));
    let changed = false;
    try {
      const response = await fetch(storefrontUrl('cart/change.js'), {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: cartSections().map((entry) => entry.id), sections_url: window.location.pathname })
      });
      const result = await cartResponse(response, message('updateError', 'Your bag could not be updated. Please try again.'));
      changed = true;
      await refreshCartDrawer(result, false, focusState);
      cartStatus(message('updated', 'Your bag is updated.'));
    } catch (error) {
      if (changed) window.location.assign(themeConfig.routes?.cart || storefrontUrl('cart'));
      else {
        qsa('[data-cart-quantity]').forEach((input) => { if (input.dataset.cartKey === key) input.value = input.defaultValue; });
        cartStatus(error.message, true);
      }
    } finally { unlock.forEach((restore) => restore()); }
  });

  const addToCart = async (form, submitter) => {
    if (form.dataset.cartPending === 'true') return;
    form.dataset.cartPending = 'true';
    const errorNode = qs('[data-product-form-error]', form);
    const statusNode = qs('[data-product-form-status]', form);
    if (errorNode) errorNode.textContent = '';
    if (statusNode) statusNode.textContent = message('adding', 'Adding to your bag…');
    const formData = new FormData(form);
    formData.set('sections', cartSections().map((entry) => entry.id).join(','));
    formData.set('sections_url', window.location.pathname);
    const label = qs('[data-submit-label]', submitter || form) || submitter;
    const originalLabel = label?.textContent;
    const unlock = lockForm(form);
    if (label) label.textContent = message('adding', 'Adding to your bag…');
    let added = false;
    try {
      await enqueueCart(async () => {
        const response = await fetch(storefrontUrl('cart/add.js'), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: formData });
        const result = await cartResponse(response, message('addError', 'This piece could not be added. Please try again.'));
        added = true;
        animateProductToCart(form);
        if (statusNode) statusNode.textContent = message('added', 'Added to your bag.');
        liveAnnounce(message('added', 'Added to your bag.'));
        await refreshCartDrawer(result, true);
        document.dispatchEvent(new CustomEvent('sidhita:quick-add', { detail: result }));
      });
    } catch (error) {
      if (added) window.location.assign(themeConfig.routes?.cart || storefrontUrl('cart'));
      else {
        if (statusNode) statusNode.textContent = '';
        if (errorNode) errorNode.textContent = error.message;
        liveAnnounce(error.message);
      }
    } finally {
      unlock();
      delete form.dataset.cartPending;
      if (label && originalLabel) label.textContent = originalLabel;
      form.dispatchEvent(new CustomEvent('sidhita:product-form-restored'));
    }
  };

  const handlePredictiveSearch = async (input) => {
    const drawer = input.closest('[data-drawer]');
    const results = qs('[data-predictive-search-results]', drawer);
    const status = qs('[data-search-status]', drawer);
    const endpoint = qs('[data-predictive-search-form]', drawer)?.dataset.predictiveUrl;
    const term = input.value.trim();
    searchAbortController?.abort();
    if (term.length < 2 || !endpoint) {
      searchAbortController = null;
      if (results) { results.textContent = message('searchMinimum', 'Type at least two characters.'); results.removeAttribute('aria-busy'); }
      if (status) status.textContent = '';
      return;
    }
    const controller = new AbortController();
    searchAbortController = controller;
    const url = `${endpoint}?q=${encodeURIComponent(term)}&section_id=predictive-search-results&resources[type]=product,collection,article,page&resources[limit]=6`;
    results?.setAttribute('aria-busy', 'true');
    if (status) status.textContent = message('searchLoading', 'Searching…');
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(message('searchError', 'Search is temporarily unavailable. You can still submit your search.'));
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = qs('[data-predictive-content]', parsed);
      if (!content) throw new Error(message('searchError', 'Search is temporarily unavailable. You can still submit your search.'));
      if (controller !== searchAbortController) return;
      if (results) results.replaceChildren(...content.childNodes);
      if (status) status.textContent = '';
    } catch (error) {
      if (error.name !== 'AbortError' && controller === searchAbortController) {
        if (results) results.textContent = '';
        if (status) status.textContent = error.message;
      }
    } finally {
      if (controller === searchAbortController) results?.removeAttribute('aria-busy');
    }
  };

  const initReveal = (context = document) => {
    const items = qsa('[data-reveal]', context).filter((item) => item.dataset.revealInitialized !== 'true');
    if (!items.length) return;
    items.forEach((item) => { item.dataset.revealInitialized = 'true'; });
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  };

  const initVideos = (context = document) => {
    const videos = qsa('video[data-lazy-video]', context).filter((video) => video.dataset.videoInitialized !== 'true');
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection?.saveData;
    const loadVideo = (video) => {
      qsa('source[data-src]', video).forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      if (!video.dataset.loaded) {
        video.load();
        video.dataset.loaded = 'true';
      }
    };
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && !reduceMotion && !saveData) {
          loadVideo(video);
          if (video.dataset.autoplay === 'true') video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { rootMargin: '200px 0px' }) : null;
    videos.forEach((video) => {
      video.dataset.videoInitialized = 'true';
      if (observer) observer.observe(video);
      else if (!reduceMotion && !saveData) {
        loadVideo(video);
        if (video.dataset.autoplay === 'true') video.play().catch(() => {});
      }
    });
    if (!videoVisibilityInitialized) {
      videoVisibilityInitialized = true;
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) qsa('video[data-lazy-video]').forEach((video) => video.pause());
      });
    }
  };

  const initDepth = () => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !matchMedia('(pointer: fine)').matches) return;
    qsa('[data-depth-root]').forEach((root) => {
      if (root.dataset.depthInitialized === 'true') return;
      root.dataset.depthInitialized = 'true';
      root.addEventListener('pointermove', (event) => {
        const bounds = root.getBoundingClientRect();
        const strength = Number(root.dataset.depthStrength || 1);
        const x = ((event.clientX - bounds.left) / bounds.width - .5) * 8 * strength;
        const y = ((event.clientY - bounds.top) / bounds.height - .5) * 5 * strength;
        root.style.setProperty('--depth-x', `${x}px`);
        root.style.setProperty('--depth-y', `${y}px`);
        root.style.setProperty('--depth-rotate-y', `${x * .08}deg`);
        root.style.setProperty('--depth-rotate-x', `${y * -.08}deg`);
      }, { passive: true });
      root.addEventListener('pointerleave', () => {
        root.style.setProperty('--depth-x', '0px');
        root.style.setProperty('--depth-y', '0px');
        root.style.setProperty('--depth-rotate-y', '0deg');
        root.style.setProperty('--depth-rotate-x', '0deg');
      }, { passive: true });
    });
  };

  const initCustomCursor = () => {
    if (customCursorInitialized || matchMedia('(prefers-reduced-motion: reduce)').matches || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const cursor = qs('[data-site-cursor]');
    if (!cursor) return;
    customCursorInitialized = true;
    document.documentElement.classList.add('custom-cursor-ready');
    let cursorFrame = null;
    let cursorX = -100;
    let cursorY = -100;
    const paintCursor = () => {
      cursor.style.left = `${cursorX}px`;
      cursor.style.top = `${cursorY}px`;
      cursorFrame = null;
    };
    document.addEventListener('pointermove', (event) => {
      cursorX = event.clientX;
      cursorY = event.clientY;
      cursor.classList.add('is-visible');
      if (!cursorFrame) cursorFrame = requestAnimationFrame(paintCursor);
    }, { passive: true });
    document.addEventListener('pointerover', (event) => {
      cursor.classList.toggle('is-interactive', Boolean(event.target.closest('a,button,input,select,textarea,summary,label,[role="button"]')));
    }, { passive: true });
    document.addEventListener('pointerdown', () => cursor.classList.add('is-pressed'), { passive: true });
    document.addEventListener('pointerup', () => cursor.classList.remove('is-pressed'), { passive: true });
    document.documentElement.addEventListener('mouseleave', () => cursor.classList.remove('is-visible'), { passive: true });
    window.addEventListener('blur', () => cursor.classList.remove('is-visible'));
  };

  const updateImmersiveScroll = () => {
    qsa('[data-immersive-hero]').forEach((hero) => {
      const bounds = hero.getBoundingClientRect();
      const distance = Math.max(0, Math.min(bounds.height, -bounds.top));
      hero.style.setProperty('--immersive-scroll', `${distance}px`);
    });
  };

  const initImmersiveHeroes = () => {
    updateImmersiveScroll();
    if (immersiveScrollInitialized || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    immersiveScrollInitialized = true;
    let ticking = false;
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateImmersiveScroll();
        ticking = false;
      });
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
  };

  const initMoodReactors = () => {
    qsa('[data-mood-reactor]').forEach((root) => {
      if (root.dataset.reactorInitialized === 'true') return;
      root.dataset.reactorInitialized = 'true';
      const triggers = qsa('[data-mood-trigger]', root);
      const panels = qsa('[data-mood-panel]', root);
      const motionToggle = qs('[data-reactor-motion-toggle]', root);
      const activeNumber = qs('[data-reactor-active-number]', root);
      const activeTitle = qs('[data-reactor-active-title]', root);
      const activeDescription = qs('[data-reactor-active-description]', root);
      const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      let activeIndex = 0;
      let cycleTimer = null;
      if (!triggers.length || !panels.length) return;

      const clearCycle = () => {
        window.clearTimeout(cycleTimer);
        cycleTimer = null;
      };

      const activate = (index, focus = false) => {
        const nextIndex = (index + triggers.length) % triggers.length;
        const nextTrigger = triggers[nextIndex];
        if (!nextTrigger) return;
        activeIndex = nextIndex;
        triggers.forEach((trigger, triggerIndex) => {
          const active = triggerIndex === nextIndex;
          trigger.classList.toggle('is-active', active);
          trigger.setAttribute('aria-selected', String(active));
          trigger.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel, panelIndex) => {
          const active = panelIndex === nextIndex;
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', String(!active));
          const panelVideo = qs('video[data-lazy-video]', panel);
          if (panelVideo) {
            if (active && !root.classList.contains('is-motion-paused') && panelVideo.dataset.loaded) panelVideo.play().catch(() => {});
            else panelVideo.pause();
          }
        });
        root.style.setProperty('--reactor-accent', nextTrigger.dataset.moodAccent);
        if (activeNumber) activeNumber.textContent = nextTrigger.dataset.moodNumber;
        if (activeTitle) activeTitle.textContent = nextTrigger.dataset.moodTitle;
        if (activeDescription) activeDescription.textContent = nextTrigger.dataset.moodDescription;
        if (focus) nextTrigger.focus();
      };

      const scheduleCycle = () => {
        clearCycle();
        if (reduceMotion || root.classList.contains('is-motion-paused') || document.hidden) return;
        cycleTimer = window.setTimeout(() => {
          activate(activeIndex + 1);
          scheduleCycle();
        }, 6000);
      };

      triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => {
          activate(index);
          scheduleCycle();
        });
        trigger.addEventListener('keydown', (event) => {
          if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = index + 1;
          if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = index - 1;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = triggers.length - 1;
          activate(nextIndex, true);
          scheduleCycle();
        });
      });

      if (motionToggle) {
        if (reduceMotion) {
          root.classList.add('is-motion-paused');
          motionToggle.setAttribute('aria-pressed', 'true');
          motionToggle.setAttribute('aria-label', 'Campaign scene is static');
          motionToggle.disabled = true;
          const label = qs('[data-reactor-motion-label]', motionToggle);
          if (label) label.textContent = 'STATIC';
        } else {
          motionToggle.addEventListener('click', () => {
            const paused = root.classList.toggle('is-motion-paused');
            motionToggle.setAttribute('aria-pressed', String(paused));
            motionToggle.setAttribute('aria-label', paused ? 'Resume changing campaign scene' : 'Pause changing campaign scene');
            const label = qs('[data-reactor-motion-label]', motionToggle);
            if (label) label.textContent = paused ? 'PLAY' : 'PAUSE';
            qsa('video[data-lazy-video]', root).forEach((video) => {
              if (paused) video.pause();
              else if (video.closest('[data-mood-panel]')?.classList.contains('is-active')) video.play().catch(() => {});
            });
            if (paused) clearCycle();
            else scheduleCycle();
          });
        }
      }

      document.addEventListener('visibilitychange', scheduleCycle);
      activate(0);
      scheduleCycle();
    });
  };

  const initLuxurySliders = () => {
    qsa('[data-luxury-slider]').forEach((root) => {
      if (root.dataset.luxuryInitialized === 'true') return;
      root.dataset.luxuryInitialized = 'true';
      const slides = qsa('[data-luxury-slide]', root);
      if (!slides.length) return;
      const current = qs('[data-luxury-current]', root);
      const status = qs('[data-luxury-status]', root);
      const toggle = qs('[data-luxury-toggle]', root);
      const toggleLabel = qs('[data-luxury-toggle-label]', root);
      const previous = qs('[data-luxury-prev]', root);
      const next = qs('[data-luxury-next]', root);
      const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.classList.contains('motion-none');
      const autoplay = root.dataset.autoplay === 'true' && slides.length > 1 && !reduceMotion;
      const duration = Number(root.dataset.duration || 8000);
      let activeIndex = 0;
      let timer = null;

      const loadVideo = (video) => {
        if (!video || video.dataset.loaded === 'true') return;
        qsa('source[data-src]', video).forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.load();
        video.dataset.loaded = 'true';
      };

      const clearTimer = () => {
        window.clearTimeout(timer);
        timer = null;
      };

      const restartProgress = () => {
        root.classList.remove('is-progressing');
        if (!autoplay || root.classList.contains('is-paused')) return;
        const progress = qs('[data-luxury-progress]', root);
        if (progress) void progress.offsetWidth;
        root.classList.add('is-progressing');
      };

      const schedule = () => {
        clearTimer();
        restartProgress();
        if (!autoplay || root.classList.contains('is-paused') || document.hidden) return;
        timer = window.setTimeout(() => {
          activate(activeIndex + 1);
        }, duration);
      };

      const activate = (index, announce = true) => {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === activeIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          const video = qs('[data-luxury-video]', slide);
          if (!video) return;
          if (active && !root.classList.contains('is-paused') && !reduceMotion) {
            loadVideo(video);
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
        const activeSlide = slides[activeIndex];
        root.style.setProperty('--luxury-accent', activeSlide.dataset.slideAccent);
        if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
        if (status && announce) {
          const heading = qs('.luxury-slider__title', activeSlide)?.textContent.trim() || '';
          status.textContent = `Showing slide ${activeIndex + 1} of ${slides.length}: ${heading}`;
        }
        schedule();
      };

      previous?.addEventListener('click', () => activate(activeIndex - 1));
      next?.addEventListener('click', () => activate(activeIndex + 1));
      let touchStartX = null;
      root.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        touchStartX = event.touches[0].clientX;
      }, { passive: true });
      root.addEventListener('touchend', (event) => {
        if (touchStartX === null || !event.changedTouches.length) return;
        const distance = event.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (Math.abs(distance) < 48) return;
        activate(activeIndex + (distance < 0 ? 1 : -1));
      }, { passive: true });
      toggle?.addEventListener('click', () => {
        const paused = root.classList.toggle('is-paused');
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
        if (toggleLabel) toggleLabel.textContent = paused ? 'PLAY' : 'PAUSE';
        qsa('[data-luxury-video]', root).forEach((video) => {
          if (paused) video.pause();
          else if (video.closest('[data-luxury-slide]')?.classList.contains('is-active')) {
            loadVideo(video);
            video.play().catch(() => {});
          }
        });
        schedule();
      });

      if (reduceMotion && toggle) {
        root.classList.add('is-paused');
        toggle.setAttribute('aria-pressed', 'true');
        toggle.setAttribute('aria-label', 'Slideshow is static');
        toggle.disabled = true;
        if (toggleLabel) toggleLabel.textContent = 'STATIC';
      }

      document.addEventListener('visibilitychange', schedule);
      activate(0, false);
    });
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'INR';
    const currencyDisplay = document.body.dataset.currencyFormat === 'code' ? 'code' : 'symbol';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay }).format(cents / 100);
    } catch (_) {
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }[character]));

  const readWishlist = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(wishlistKey) || '[]');
      return [...new Set(stored.filter((handle) => typeof handle === 'string' && handle))].slice(0, 50);
    } catch (_) {
      return [];
    }
  };

  const writeWishlist = (handles) => {
    try { localStorage.setItem(wishlistKey, JSON.stringify(handles)); } catch (_) {}
  };

  const updateWishlistUI = (handles = readWishlist()) => {
    qsa('[data-wishlist-count]').forEach((count) => {
      count.textContent = handles.length;
      count.setAttribute('aria-label', `${handles.length} saved pieces`);
    });
    qsa('[data-wishlist-handle]').forEach((button) => {
      const saved = handles.includes(button.dataset.wishlistHandle);
      button.classList.toggle('is-active', saved);
      button.setAttribute('aria-pressed', String(saved));
      const label = qs('[data-wishlist-label]', button);
      if (label) label.textContent = saved ? 'Saved' : 'Save this piece';
      const title = button.dataset.wishlistTitle || 'piece';
      button.setAttribute('aria-label', saved ? `Remove ${title} from saved pieces` : `Save ${title}`);
    });
  };

  const renderWishlist = async () => {
    const content = qs('[data-wishlist-content]');
    if (!content) return;
    content.dataset.emptyHtml ||= content.innerHTML;
    const handles = readWishlist();
    updateWishlistUI(handles);
    if (!handles.length) {
      content.innerHTML = content.dataset.emptyHtml;
      return;
    }
    content.setAttribute('aria-busy', 'true');
    const products = await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        return await response.json();
      } catch (_) {
        return null;
      }
    }));
    const availableProducts = products.filter(Boolean);
    if (!availableProducts.length) {
      content.innerHTML = content.dataset.emptyHtml;
      content.removeAttribute('aria-busy');
      return;
    }
    content.innerHTML = `<div class="wishlist-drawer__items">${availableProducts.map((product) => {
      const title = escapeHtml(product.title);
      const handle = escapeHtml(product.handle);
      const url = escapeHtml(product.url || `/products/${product.handle}`);
      let image = '';
      const featuredImage = product.featured_image || (product.handle === 'vihana-inkline-utility-dress' ? content.dataset.vihanaImage : '');
      if (featuredImage) {
        try {
          const imageUrl = new URL(featuredImage, window.location.origin);
          imageUrl.searchParams.set('width', '360');
          image = `<img src="${escapeHtml(imageUrl.toString())}" alt="" width="360" height="450" loading="lazy">`;
        } catch (_) {
          const separator = featuredImage.includes('?') ? '&' : '?';
          image = `<img src="${escapeHtml(`${featuredImage}${separator}width=360`)}" alt="" width="360" height="450" loading="lazy">`;
        }
      }
      return `<article class="wishlist-item">
        <a class="wishlist-item__media" href="${url}">${image}</a>
        <div class="wishlist-item__information">
          <a class="wishlist-item__title" href="${url}">${title}</a>
          <span class="wishlist-item__price">${formatMoney(product.price)}</span>
          <a class="text-link" href="${url}">View piece</a>
          <button class="text-button wishlist-item__remove" type="button" data-wishlist-remove data-wishlist-handle="${handle}" data-wishlist-title="${title}">Remove</button>
        </div>
      </article>`;
    }).join('')}</div>`;
    content.removeAttribute('aria-busy');
  };

  const toggleWishlist = (button) => {
    const handle = button.dataset.wishlistHandle;
    if (!handle) return;
    const handles = readWishlist();
    const saved = handles.includes(handle);
    const next = saved ? handles.filter((item) => item !== handle) : [handle, ...handles];
    writeWishlist(next);
    updateWishlistUI(next);
    const title = button.dataset.wishlistTitle || 'Piece';
    liveAnnounce(saved ? `${title} removed from saved pieces` : `${title} saved`);
    if (qs('#WishlistDrawer.is-open')) renderWishlist();
  };

  const loadQuickView = async (trigger) => {
    const drawer = qs('#QuickViewDrawer');
    const content = qs('[data-quick-view-content]', drawer);
    if (!drawer || !content) return;
    openDrawer(drawer, trigger);
    content.setAttribute('aria-busy', 'true');
    content.innerHTML = '<p class="quick-view-drawer__loading">Loading…</p>';
    try {
      const url = new URL(trigger.dataset.quickViewUrl, window.location.origin);
      url.searchParams.set('section_id', 'quick-view-product');
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Quick view unavailable');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const product = qs('[data-quick-view-product]', parsed);
      if (!product) throw new Error('Quick view unavailable');
      content.innerHTML = product.outerHTML;
    } catch (_) {
      content.innerHTML = `<p class="quick-view-drawer__loading">${escapeHtml(content.dataset.errorMessage || 'Quick view is temporarily unavailable.')}</p>`;
    } finally {
      content.removeAttribute('aria-busy');
    }
  };

  const showPrivacyBanner = () => {
    const banner = qs('[data-privacy-banner]');
    if (!banner) return;
    banner.hidden = false;
    requestAnimationFrame(() => banner.classList.add('is-visible'));
  };

  const hidePrivacyBanner = () => {
    const banner = qs('[data-privacy-banner]');
    if (!banner) return;
    banner.classList.remove('is-visible');
    window.setTimeout(() => { banner.hidden = true; }, 320);
  };

  const syncPrivacyControls = () => {
    const banner = qs('[data-privacy-banner]');
    if (!banner || !privacyApi) return;
    const consent = privacyApi.currentVisitorConsent();
    const preferences = qs('[data-privacy-preferences]', banner);
    const analytics = qs('[data-privacy-analytics]', banner);
    const marketing = qs('[data-privacy-marketing]', banner);
    if (preferences) preferences.checked = consent.preferences === 'yes';
    if (analytics) analytics.checked = consent.analytics === 'yes';
    if (marketing) marketing.checked = consent.marketing === 'yes';
  };

  const setPrivacyConsent = (consent) => {
    if (!privacyApi) return;
    privacyApi.setTrackingConsent(consent, () => {
      syncPrivacyControls();
      hidePrivacyBanner();
      liveAnnounce('Privacy preferences saved');
    });
  };

  const initPrivacy = () => {
    const banner = qs('[data-privacy-banner]');
    if (!banner || typeof window.Shopify?.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error || !window.Shopify.customerPrivacy) return;
      privacyApi = window.Shopify.customerPrivacy;
      syncPrivacyControls();
      if (privacyApi.shouldShowBanner()) showPrivacyBanner();
    });
  };

  const updateProductVariant = (input) => {
    const section = input.closest('[data-product-section]');
    if (!section) return;
    const variantsNode = qs('[data-product-json]', section);
    if (!variantsNode) return;
    const variants = JSON.parse(variantsNode.textContent);
    const selections = qsa('.product-option', section).map((option) => qs('[data-product-option]:checked', option)?.value);
    const variant = variants.find((item) => item.options.every((value, index) => value === selections[index]));
    const idInput = qs('[data-variant-id]', section);
    const addButton = qs('[data-add-to-cart]', section);
    const mobileButton = qs('.product-mobile-bar .button', section);
    const price = qs('[data-product-price]', section);
    const mobilePrice = qs('[data-mobile-product-price]', section);
    const backInStock = qs('[data-back-in-stock]', section);
    const backInStockVariant = qs('[data-back-in-stock-variant]', section);
    const lowStock = qs('[data-low-stock]', section);
    const dynamicCheckout = qs('[data-dynamic-checkout]', section);
    const inventoryNode = qs('[data-variant-inventory-json]', section);
    const inventory = inventoryNode ? JSON.parse(inventoryNode.textContent) : {};
    const formError = qs('[data-product-form-error]', section);
    if (formError) formError.textContent = '';
    qsa('.product-option', section).forEach((option) => {
      const label = qs('[data-selected-option]', option);
      const checked = qs('[data-product-option]:checked', option);
      if (label && checked) label.textContent = checked.value;
    });
    if (!variant) {
      if (idInput) idInput.value = '';
      if (lowStock) lowStock.hidden = true;
      if (backInStock) backInStock.hidden = true;
      if (dynamicCheckout) dynamicCheckout.hidden = true;
      [addButton, mobileButton].forEach((button) => {
        if (!button) return;
        button.disabled = true;
        button.textContent = section.dataset.unavailableLabel;
      });
      return;
    }
    if (idInput) idInput.value = variant.id;
    const available = Boolean(variant.available);
    [addButton, mobileButton].forEach((button) => {
      if (!button) return;
      button.disabled = !available;
      button.textContent = available ? section.dataset.addLabel : section.dataset.soldOutLabel;
    });
    if (price) {
      price.innerHTML = variant.compare_at_price > variant.price
        ? `<div class="price price--sale"><s>${formatMoney(variant.compare_at_price)}</s><span class="price__current">${formatMoney(variant.price)}</span></div>`
        : `<div class="price"><span class="price__current">${formatMoney(variant.price)}</span></div>`;
    }
    if (mobilePrice) mobilePrice.textContent = formatMoney(variant.price);
    if (lowStock) {
      const quantity = Number(inventory[String(variant.id)] || 0);
      const threshold = Number(section.dataset.lowStockThreshold || 0);
      const showLowStock = available && quantity > 0 && quantity <= threshold;
      lowStock.hidden = !showLowStock;
      if (showLowStock) lowStock.textContent = (section.dataset.lowStockTemplate || 'Only __COUNT__ left').replace('__COUNT__', quantity);
    }
    if (backInStock) backInStock.hidden = available;
    if (dynamicCheckout) dynamicCheckout.hidden = !available;
    if (backInStockVariant) backInStockVariant.value = `${variant.title} / ${variant.id}`;
    if (variant.featured_media?.id) qs(`[data-media-id="${variant.featured_media.id}"]`, section)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    document.dispatchEvent(new CustomEvent('sidhita:variant-changed', { detail: variant }));
  };

  const initRecommendations = () => {
    qsa('[data-product-recommendations]').forEach((section) => {
      if (section.dataset.loaded === 'true') return;
      const load = async () => {
        section.dataset.loaded = 'true';
        try {
          const html = await fetch(section.dataset.url).then((response) => response.text());
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const replacement = qs('[data-product-recommendations]', parsed);
          const currentCards = qsa('.product-card', section).length;
          const replacementCards = replacement ? qsa('.product-card', replacement).length : 0;
          if (replacement?.innerHTML.trim() && replacementCards >= currentCards) section.innerHTML = replacement.innerHTML;
        } catch (_) {}
      };
      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      }, { rootMargin: '300px 0px' });
      observer.observe(section);
    });
  };

  const initRecentlyViewed = async () => {
    const section = qs('[data-recently-viewed]');
    if (!section) return;
    const current = section.dataset.currentHandle;
    let handles = [];
    try { handles = JSON.parse(localStorage.getItem('sidhita-recent-products') || '[]'); } catch (_) {}
    const previous = handles.filter((handle) => handle && handle !== current).slice(0, 4);
    if (previous.length) {
      const cards = await Promise.all(previous.map(async (handle) => {
        try {
          const html = await fetch(`/products/${encodeURIComponent(handle)}?section_id=recently-viewed-card`).then((response) => response.text());
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          return qs('[data-recent-product]', parsed)?.innerHTML || '';
        } catch (_) { return ''; }
      }));
      const grid = qs('[data-recently-viewed-grid]', section);
      if (grid && cards.some(Boolean)) {
        grid.innerHTML = cards.join('');
        section.hidden = false;
        updateWishlistUI();
      }
    }
    try { localStorage.setItem('sidhita-recent-products', JSON.stringify([current, ...previous].slice(0, 5))); } catch (_) {}
  };

  document.addEventListener('click', (event) => {
    const recommendationStep = event.target.closest('[data-recommendation-step]');
    if (recommendationStep) {
      const section = recommendationStep.closest('[data-product-recommendations]');
      const track = qs('[data-recommendation-track]', section);
      const card = qs('.product-card', track);
      if (!track || !card) return;
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 12;
      const distance = card.getBoundingClientRect().width + gap;
      track.scrollBy({ left: Number(recommendationStep.dataset.recommendationStep) * distance, behavior: 'smooth' });
      return;
    }
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const target = document.getElementById(opener.dataset.drawerOpen);
      if (!target) return;
      event.preventDefault();
      openDrawer(target, opener);
      return;
    }
    const wishlist = event.target.closest('[data-wishlist-handle]');
    if (wishlist) {
      event.preventDefault();
      toggleWishlist(wishlist);
      return;
    }
    const quickView = event.target.closest('[data-quick-view-url]');
    if (quickView) {
      event.preventDefault();
      loadQuickView(quickView);
      return;
    }
    const privacyOpen = event.target.closest('[data-privacy-open]');
    if (privacyOpen) {
      syncPrivacyControls();
      showPrivacyBanner();
      return;
    }
    const privacyAction = event.target.closest('[data-privacy-action]');
    if (privacyAction) {
      const action = privacyAction.dataset.privacyAction;
      if (action === 'accept') setPrivacyConsent({ preferences: true, analytics: true, marketing: true });
      if (action === 'decline') setPrivacyConsent({ preferences: false, analytics: false, marketing: false });
      if (action === 'save') {
        const banner = privacyAction.closest('[data-privacy-banner]');
        setPrivacyConsent({
          preferences: Boolean(qs('[data-privacy-preferences]', banner)?.checked),
          analytics: Boolean(qs('[data-privacy-analytics]', banner)?.checked),
          marketing: Boolean(qs('[data-privacy-marketing]', banner)?.checked)
        });
      }
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      closeDrawer(closer.closest('[data-drawer]'));
      return;
    }
    const railControl = event.target.closest('[data-rail-direction]');
    if (railControl) {
      const rail = document.getElementById(railControl.getAttribute('aria-controls'));
      rail?.scrollBy({ left: rail.clientWidth * .8 * Number(railControl.dataset.railDirection), behavior: 'smooth' });
      return;
    }
    const quantityButton = event.target.closest('[data-quantity-plus],[data-quantity-minus]');
    if (quantityButton) {
      const wrapper = quantityButton.closest('[data-quantity]');
      const input = qs('input[type="number"]', wrapper);
      if (!input) return;
      const direction = quantityButton.hasAttribute('data-quantity-plus') ? 1 : -1;
      if (input.disabled) return;
      const maximum = input.max ? Number(input.max) : Infinity;
      const next = Math.min(maximum, Math.max(Number(input.min || 0), Number(input.value || 0) + direction));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      if (remove.closest('[aria-busy="true"]')) return;
      changeCartLine(remove.dataset.cartKey, 0);
      return;
    }
    const videoControl = event.target.closest('[data-video-control]');
    if (videoControl) {
      const hero = videoControl.closest('.hero-video, .immersive-hero');
      const videos = qsa('video[data-lazy-video]', hero);
      const video = videos.find((item) => getComputedStyle(item).display !== 'none') || videos[0];
      if (!video) return;
      if (videoControl.dataset.videoControl === 'play') {
        const syncPlayControl = () => {
          const isPaused = video.paused;
          videoControl.setAttribute('aria-pressed', String(isPaused));
          videoControl.setAttribute('aria-label', isPaused ? videoControl.dataset.playLabel : videoControl.dataset.pauseLabel);
        };
        if (video.paused) {
          video.play().then(syncPlayControl).catch(syncPlayControl);
        } else {
          video.pause();
          syncPlayControl();
        }
      } else {
        video.muted = !video.muted;
        videoControl.setAttribute('aria-pressed', String(video.muted));
        videoControl.setAttribute('aria-label', video.muted ? videoControl.dataset.unmuteLabel : videoControl.dataset.muteLabel);
      }
      return;
    }
    const zoomTrigger = event.target.closest('[data-media-zoom]');
    if (zoomTrigger) {
      const dialog = qs('[data-media-zoom-dialog]');
      const image = qs('[data-media-zoom-image]', dialog);
      if (dialog && image) {
        image.src = zoomTrigger.dataset.mediaZoom;
        image.alt = zoomTrigger.dataset.mediaAlt || '';
        document.documentElement.classList.add('media-zoom-open');
        dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('media-zoom-open');
        }, { once: true });
        dialog.showModal();
      }
      return;
    }
    const zoomClose = event.target.closest('[data-media-zoom-close]');
    if (zoomClose) {
      zoomClose.closest('dialog')?.close();
      return;
    }
    const zoomDialog = event.target.closest('[data-media-zoom-dialog]');
    if (zoomDialog && event.target === zoomDialog) {
      zoomDialog.close();
      return;
    }
    const share = event.target.closest('[data-share-url]');
    if (share) {
      const data = { title: document.title, url: share.dataset.shareUrl };
      if (navigator.share) navigator.share(data).catch(() => {});
      else if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(data.url)
          .then(() => liveAnnounce('Link copied'))
          .catch(() => liveAnnounce('Unable to copy the link'));
      } else {
        liveAnnounce('Copy the link from your address bar');
      }
      return;
    }
    const orderPrint = event.target.closest('[data-order-print]');
    if (orderPrint) {
      const originalTitle = document.title;
      const invoiceTitle = orderPrint.dataset.orderPrintTitle;
      if (invoiceTitle) document.title = invoiceTitle;
      window.addEventListener('afterprint', () => { document.title = originalTitle; }, { once: true });
      window.print();
      return;
    }
    const customerToggle = event.target.closest('[data-customer-toggle]');
    if (customerToggle) {
      event.preventDefault();
      const recover = qs('#RecoverPassword');
      const login = qs('#Login');
      if (recover && login) {
        const showRecover = customerToggle.getAttribute('href') === '#RecoverPassword';
        recover.hidden = !showRecover;
        login.hidden = showRecover;
      }
      return;
    }
    const addressToggle = event.target.closest('[data-address-toggle]');
    if (addressToggle) {
      const target = document.getElementById(addressToggle.dataset.addressToggle);
      if (target) target.hidden = !target.hidden;
      return;
    }
    const confirmDelete = event.target.closest('[data-confirm-delete]');
    if (confirmDelete && !window.confirm('Delete this address?')) {
      event.preventDefault();
    }
  });

  document.addEventListener('keydown', (event) => {
    const drawer = qs('[data-drawer].is-open');
    if (!drawer) return;
    if (event.key === 'Escape') closeDrawer(drawer);
    trapDrawerFocus(event, drawer);
  });

  document.addEventListener('change', (event) => {
    const localization = event.target.closest('[data-localization-select]');
    if (localization) {
      localization.form?.submit();
      return;
    }
    const cartQuantity = event.target.closest('[data-cart-quantity]');
    if (cartQuantity) {
      if (!cartQuantity.checkValidity()) { cartQuantity.reportValidity(); return; }
      changeCartLine(cartQuantity.dataset.cartKey, Number(cartQuantity.value));
      return;
    }
    const productOption = event.target.closest('[data-product-option]');
    if (productOption) {
      updateProductVariant(productOption);
      return;
    }
    const sortSelect = event.target.closest('[data-sort-select]');
    if (sortSelect) {
      sortSelect.form?.submit();
    }
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-predictive-search-input]');
    if (!input) return;
    window.clearTimeout(input.searchTimer);
    input.searchTimer = window.setTimeout(() => handlePredictiveSearch(input), 180);
  });

  document.addEventListener('submit', (event) => {
    const trackingSupportForm = event.target.closest('[data-tracking-support-form]');
    if (trackingSupportForm) {
      event.preventDefault();
      const orderReference = trackingSupportForm.elements.order?.value?.trim();
      const contact = trackingSupportForm.elements.contact?.value?.trim();
      const whatsappNumber = trackingSupportForm.dataset.whatsappNumber;
      if (!orderReference || !contact || !whatsappNumber) return;
      const message = `Hello Sidhita, please help me track order ${orderReference}. My registered contact is ${contact}.`;
      window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
      liveAnnounce('Opening verified WhatsApp order support');
      return;
    }
    if (event.defaultPrevented) return;
    const cartForm = event.target.closest('[data-cart-form]');
    if (cartForm) {
      if (cartForm.getAttribute('aria-busy') === 'true') { event.preventDefault(); return; }
      cartForm.setAttribute('aria-busy', 'true');
      if (event.submitter?.name === 'checkout') {
        event.submitter.dataset.checkoutLabel = event.submitter.textContent;
        event.submitter.textContent = message('checkoutLoading', 'Opening secure checkout…');
      }
      return;
    }
    const form = event.target.closest('[data-ajax-cart]');
    if (!form || document.body.dataset.cartDrawer !== 'true' || !qs('#CartDrawer')) return;
    if (event.submitter?.name && event.submitter.name !== 'add') return;
    event.preventDefault();
    if (!form.checkValidity() || event.submitter?.disabled) { form.reportValidity(); return; }
    addToCart(form, event.submitter);
  });

  document.addEventListener('DOMContentLoaded', () => {
    finishPreloader();
    initPageTransitions();
    updateHeader();
    initReveal();
    initVideos();
    initDepth();
    initCustomCursor();
    initImmersiveHeroes();
    initMoodReactors();
    initLuxurySliders();
    initRecommendations();
    initRecentlyViewed();
    updateWishlistUI();
    initPrivacy();
    qsa('[data-predictive-search-form]').forEach((form) => {
      form.dataset.predictiveUrl ||= storefrontUrl('search/suggest');
    });
  });
  window.addEventListener('scroll', updateHeader, { passive: true });
  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    initVideos(event.target);
    initDepth(event.target);
    initImmersiveHeroes(event.target);
    initMoodReactors(event.target);
    initLuxurySliders(event.target);
    initRecommendations(event.target);
    updateWishlistUI();
  });
  document.addEventListener('sidhita:drawer-opened', (event) => {
    if (event.detail?.id === 'WishlistDrawer') renderWishlist();
  });
})();
